<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Usuarios</h1>
      <button class="btn-primary" @click="openCreate">+ Novo usuario</button>
    </div>

    <p class="page-help">
      <strong>Administradores</strong> podem criar/editar eventos, planos e veem valores financeiros.
      <strong>Voluntarios</strong> so veem a lista de inscritos (sem valores).
    </p>

    <SkeletonLoader v-if="loading" variant="row" :count="4" />

    <div v-else-if="admins.length" class="table-card card">
      <div class="table-wrap"><table class="table">
        <thead>
          <tr><th>Nome</th><th>Email</th><th>Papel</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="u in admins" :key="u.id">
            <td>
              {{ u.name }}
              <small v-if="u.role === 'CHECKIN' && u.eventoCheckin" class="evt-tag">
                · {{ u.eventoCheckin.nome }}
              </small>
              <small v-else-if="u.role === 'CHECKIN'" class="evt-tag warn">
                · sem evento
              </small>
            </td>
            <td class="muted">{{ u.email }}</td>
            <td><span class="chip" :class="`chip-${u.role.toLowerCase()}`">{{ roleLabel(u.role) }}</span></td>
            <td>
              <button v-if="u.role === 'CHECKIN' && canRemove(u)" class="link-btn" @click="openVincular(u)">
                Evento
              </button>
              <button class="link-btn" :disabled="!canChangePassword(u)" @click="openPwd(u)">Senha</button>
              <button class="link-btn link-danger" :disabled="!canRemove(u)" @click="confirmDelete(u)">Remover</button>
            </td>
          </tr>
        </tbody>
      </table></div>
    </div>

    <EmptyState v-else icon="👥" title="Nenhum usuario" />

    <!-- Create modal -->
    <div v-if="showCreate" class="modal-overlay" @click.self="closeCreate">
      <div class="modal-card">
        <h2 class="modal-title">Novo usuario</h2>
        <form @submit.prevent="handleCreate">
          <div class="form-row"><label class="label">Nome</label><input class="input" v-model="form.name" required /></div>
          <div class="form-row"><label class="label">Email</label><input class="input" type="email" v-model="form.email" required /></div>
          <div class="form-row"><label class="label">Senha</label><input class="input" type="password" v-model="form.password" required minlength="6" /></div>
          <div class="form-row">
            <label class="label">Papel</label>
            <select class="select" v-model="form.role">
              <option value="CHECKIN">Voluntário (só check-in)</option>
              <option value="ORGANIZADOR">Organizador de evento (vê financeiro do próprio evento)</option>
              <option v-if="authStore.isRoot" value="ADMIN">Administrador (acesso total)</option>
            </select>
            <small v-if="form.role === 'ORGANIZADOR'" class="hint">
              Após criar, vincule esse usuário a um evento em /admin/eventos &gt; aba "Comissão & repasse".
            </small>
          </div>

          <div v-if="form.role === 'CHECKIN'" class="form-row">
            <label class="label">Evento de check-in</label>
            <select class="select" v-model="form.eventoCheckinId" required>
              <option value="" disabled>Selecione um evento</option>
              <option v-for="e in eventosComCheckin" :key="e.id" :value="e.id">{{ e.nome }}</option>
            </select>
            <small class="hint">
              Cada voluntário só pode atuar em 1 evento. Só aparecem eventos com check-in habilitado.
            </small>
          </div>
          <div v-if="createError" class="error-msg">{{ createError }}</div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeCreate">Cancelar</button>
            <button type="submit" class="btn-primary" :disabled="creating">{{ creating ? 'Salvando...' : 'Criar' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Password modal -->
    <div v-if="pwdTarget" class="modal-overlay" @click.self="pwdTarget = null">
      <div class="modal-card">
        <h2 class="modal-title">Trocar senha de {{ pwdTarget.name }}</h2>
        <form @submit.prevent="handlePwd">
          <div class="form-row"><label class="label">Nova senha</label><input class="input" type="password" v-model="newPassword" required minlength="6" /></div>
          <div v-if="pwdError" class="error-msg">{{ pwdError }}</div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="pwdTarget = null">Cancelar</button>
            <button type="submit" class="btn-primary" :disabled="changing">{{ changing ? 'Salvando...' : 'Atualizar' }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="vincularTarget" class="modal-overlay" @click.self="vincularTarget = null">
      <div class="modal-card">
        <h2 class="modal-title">Vincular {{ vincularTarget.name }} a um evento</h2>
        <form @submit.prevent="handleVincular">
          <div class="form-row">
            <label class="label">Evento de check-in</label>
            <select class="select" v-model="vincularEventoId" required>
              <option value="" disabled>Selecione um evento</option>
              <option v-for="e in eventosComCheckin" :key="e.id" :value="e.id">{{ e.nome }}</option>
            </select>
            <small class="hint">Cada voluntário só pode atuar em 1 evento.</small>
          </div>
          <div v-if="vincularError" class="error-msg">{{ vincularError }}</div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="vincularTarget = null">Cancelar</button>
            <button type="submit" class="btn-primary" :disabled="vincularSaving">{{ vincularSaving ? 'Salvando...' : 'Salvar' }}</button>
          </div>
        </form>
      </div>
    </div>

    <ConfirmDialog
      :visible="deleteTarget !== null"
      :title="`Remover ${deleteTarget?.name || ''}?`"
      message="Esta acao nao pode ser desfeita."
      confirmLabel="Remover"
      @confirm="handleDelete"
      @cancel="deleteTarget = null"
    />
  </div>
</template>

<script>
import api from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import ConfirmDialog from '../../components/ui/ConfirmDialog.vue'
import EmptyState from '../../components/ui/EmptyState.vue'

const ROLE_LABEL = { ROOT: 'Root', ADMIN: 'Admin', CHECKIN: 'Voluntario', ORGANIZADOR: 'Organizador' }

export default {
  name: 'UsuariosPage',
  components: { SkeletonLoader, ConfirmDialog, EmptyState },
  data() {
    return {
      admins: [], loading: false,
      eventos: [],
      showCreate: false, creating: false, createError: '',
      form: { name: '', email: '', password: '', role: 'CHECKIN', eventoCheckinId: '' },
      pwdTarget: null, newPassword: '', changing: false, pwdError: '',
      vincularTarget: null, vincularEventoId: '', vincularSaving: false, vincularError: '',
      deleteTarget: null
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    eventosComCheckin() {
      return this.eventos.filter(e => e.temCheckin && e.status !== 'ARQUIVADO')
    }
  },
  async mounted() {
    await this.fetch()
    try {
      const { data } = await api.get('/eventos')
      this.eventos = data.eventos
    } catch {}
  },
  methods: {
    roleLabel(r) { return ROLE_LABEL[r] || r },
    canRemove(u) {
      if (u.id === this.authStore.admin?.id) return false
      if (u.role === 'ROOT') return false
      if (this.authStore.isRoot) return true
      if (this.authStore.isAdmin) return u.role === 'CHECKIN'
      return false
    },
    canChangePassword(u) {
      if (u.id === this.authStore.admin?.id) return true
      if (this.authStore.isRoot) return true
      if (this.authStore.isAdmin) return u.role === 'CHECKIN'
      return false
    },
    async fetch() {
      this.loading = true
      try {
        const { data } = await api.get('/admins')
        this.admins = data.admins
      } catch {
        this.$toast.open({ message: 'Erro ao carregar', type: 'error', position: 'top-right' })
      } finally { this.loading = false }
    },
    openCreate() {
      this.form = { name: '', email: '', password: '', role: 'CHECKIN', eventoCheckinId: '' }
      this.createError = ''
      this.showCreate = true
    },
    closeCreate() { this.showCreate = false },
    async handleCreate() {
      this.creating = true
      this.createError = ''
      try {
        if (this.form.role === 'CHECKIN' && !this.form.eventoCheckinId) {
          this.createError = 'Voluntário precisa estar vinculado a um evento'
          this.creating = false
          return
        }
        const payload = {
          name: this.form.name,
          email: this.form.email,
          password: this.form.password,
          role: this.form.role,
          ...(this.form.role === 'CHECKIN' && { eventoCheckinId: this.form.eventoCheckinId })
        }
        await api.post('/admins', payload)
        await this.fetch()
        this.closeCreate()
        this.$toast.open({ message: 'Usuario criado', type: 'success', position: 'top-right' })
      } catch (err) {
        this.createError = err.response?.data?.message || 'Erro ao criar'
      } finally { this.creating = false }
    },
    openPwd(u) { this.pwdTarget = u; this.newPassword = ''; this.pwdError = '' },
    openVincular(u) {
      this.vincularTarget = u
      this.vincularEventoId = u.eventoCheckinId || ''
      this.vincularError = ''
    },
    async handleVincular() {
      this.vincularSaving = true
      this.vincularError = ''
      try {
        await api.patch(`/admins/${this.vincularTarget.id}`, { eventoCheckinId: this.vincularEventoId })
        this.vincularTarget = null
        await this.fetch()
        this.$toast.open({ message: 'Vinculação atualizada', type: 'success', position: 'top-right' })
      } catch (err) {
        this.vincularError = err.response?.data?.message || 'Erro ao vincular'
      } finally { this.vincularSaving = false }
    },
    async handlePwd() {
      this.changing = true
      try {
        await api.patch(`/admins/${this.pwdTarget.id}/password`, { password: this.newPassword })
        this.pwdTarget = null
        this.$toast.open({ message: 'Senha atualizada', type: 'success', position: 'top-right' })
      } catch (err) {
        this.pwdError = err.response?.data?.message || 'Erro'
      } finally { this.changing = false }
    },
    confirmDelete(u) { this.deleteTarget = u },
    async handleDelete() {
      const u = this.deleteTarget
      this.deleteTarget = null
      try {
        await api.delete(`/admins/${u.id}`)
        this.admins = this.admins.filter(a => a.id !== u.id)
        this.$toast.open({ message: 'Usuario removido', type: 'info', position: 'top-right' })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    }
  }
}
</script>

<style scoped>
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--spacing-base); }
.page-header .page-title { margin-bottom: 0; }
.page-help { font-size: var(--text-sm); color: var(--color-text-muted); margin-bottom: var(--spacing-lg); line-height: 1.5; }
.table-card { padding: 0; overflow-x: auto; }
.table { width: 100%; border-collapse: collapse; }
.table th {
  text-align: left; padding: 10px var(--spacing-base);
  font-size: var(--text-xs); color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 2px solid var(--color-bg-separator);
}
.table td {
  padding: 12px var(--spacing-base);
  font-size: var(--text-sm);
  border-bottom: 1px solid var(--color-bg-separator);
}
.muted { color: var(--color-text-muted); }
.chip {
  display: inline-block;
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  padding: 2px 8px; border-radius: 4px;
}
.chip-root { background: #fde68a; color: #78350f; }
.chip-admin { background: #dbeafe; color: #1d4ed8; }
.chip-checkin { background: #f3f4f6; color: #4b5563; }
.evt-tag { color: var(--color-text-muted); font-size: 11px; margin-left: 4px; }
.evt-tag.warn { color: var(--color-warning); }
.chip-organizador { background: #ede9fe; color: #6d28d9; }
.hint { color: var(--color-text-muted); font-size: 11px; display: inline-block; margin-top: 4px; }
.link-btn {
  background: none; border: none; cursor: pointer;
  color: var(--color-accent); font-size: var(--text-xs); padding: 4px 8px;
}
.link-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.link-danger { color: var(--color-danger); }
.form-row { margin-bottom: var(--spacing-md); display: flex; flex-direction: column; gap: 4px; }
</style>
